import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import jsQR from 'jsqr';

interface ScanResult {
  tipo: 'VEICULO' | 'EQUIPAMENTO';
  id: number;
  placa?: string;
  tag?: string;
  marca?: string;
  modelo?: string;
  nome?: string;
  tipo_equipamento?: string;
  localizacao?: string;
  empresa_id: number;
  empresa_nome?: string;
}

interface MotoristaInfo {
  id: number;
  nome: string;
  celular: string;
  empresa_id: number;
  empresa_nome?: string;
}

const QRScanner: React.FC = () => {
  const [searchParams] = useSearchParams();
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const scanningRef = useRef<boolean>(false);
  const animationFrameRef = useRef<number | null>(null);

  const [isScanning, setIsScanning] = useState(false);
  const [hasCamera, setHasCamera] = useState(true);
  const [scanResult, setScanResult] = useState<ScanResult | null>(null);
  const [motorista, setMotorista] = useState<MotoristaInfo | null>(null);
  const [step, setStep] = useState<'scan' | 'identify' | 'success' | 'error'>('scan');
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [telefone, setTelefone] = useState('');
  const [manualTag, setManualTag] = useState('');
  const [showManualInput, setShowManualInput] = useState(false);
  const [detectedQRData, setDetectedQRData] = useState<string | null>(null);

  // Obter parâmetros da URL (se vieram do WhatsApp)
  const phoneFromUrl = searchParams.get('phone');
  const empresaIdFromUrl = searchParams.get('empresa_id');

  // Determinar URL base da API - sem o sufixo /api pois as rotas públicas são /api/public/qr/*
  const API_BASE_URL = process.env.REACT_APP_ENVIRONMENT === 'development'
    ? (process.env.REACT_APP_API_BASE_URL_DEV?.replace('/api', '') || 'http://localhost:3001')
    : (process.env.REACT_APP_API_BASE_URL?.replace('/api', '') || 'https://api-confereai.robotia.com.br');

  useEffect(() => {
    // Se veio telefone na URL, preencher automaticamente
    if (phoneFromUrl) {
      setTelefone(phoneFromUrl);
    }
  }, [phoneFromUrl]);

  const stopCamera = useCallback(() => {
    scanningRef.current = false;
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }
    if (videoRef.current && videoRef.current.srcObject) {
      const stream = videoRef.current.srcObject as MediaStream;
      stream.getTracks().forEach(track => track.stop());
      videoRef.current.srcObject = null;
    }
    setIsScanning(false);
  }, []);

  const scanQRCode = useCallback(() => {
    if (!videoRef.current || !canvasRef.current) return;

    const video = videoRef.current;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');

    if (!ctx) return;

    const scan = () => {
      if (!scanningRef.current) return;

      if (!video.videoWidth || !video.videoHeight) {
        animationFrameRef.current = requestAnimationFrame(scan);
        return;
      }

      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

      const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);

      // Usar jsQR para detectar QR Code
      const code = jsQR(imageData.data, imageData.width, imageData.height, {
        inversionAttempts: 'dontInvert',
      });

      if (code && code.data) {
        // QR Code detectado!
        console.log('QR Code detectado:', code.data);
        scanningRef.current = false;
        stopCamera();
        setDetectedQRData(code.data);
        return;
      }

      animationFrameRef.current = requestAnimationFrame(scan);
    };

    animationFrameRef.current = requestAnimationFrame(scan);
  }, [stopCamera]);

  const startCamera = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'environment' }
      });

      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        videoRef.current.play();
        setIsScanning(true);
        setHasCamera(true);
        scanningRef.current = true;
        scanQRCode();
      }
    } catch (err) {
      console.error('Erro ao acessar câmera:', err);
      setHasCamera(false);
      setShowManualInput(true);
    }
  }, [scanQRCode]);

  // Processar QR Code quando detectado
  useEffect(() => {
    if (detectedQRData) {
      processQRCode(detectedQRData);
      setDetectedQRData(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [detectedQRData]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopCamera();
    };
  }, [stopCamera]);

  const processQRCode = async (qrData: string) => {
    try {
      setIsLoading(true);
      setError(null);

      const response = await fetch(`${API_BASE_URL}/api/public/qr/scan`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ qr_data: qrData })
      });

      const data = await response.json();

      if (data.success) {
        setScanResult(data.data);
        stopCamera();

        // Se já temos o telefone, identificar motorista automaticamente
        if (phoneFromUrl) {
          await identifyMotorista(phoneFromUrl);
        } else {
          setStep('identify');
        }
      } else {
        setError(data.message || 'QR Code inválido');
      }
    } catch (err) {
      console.error('Erro ao processar QR Code:', err);
      setError('Erro ao processar QR Code. Tente novamente.');
    } finally {
      setIsLoading(false);
    }
  };

  const identifyMotorista = async (phone: string) => {
    try {
      setIsLoading(true);
      setError(null);

      const normalizedPhone = phone.replace(/\D/g, '');

      const response = await fetch(`${API_BASE_URL}/api/public/qr/motorista`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          telefone: normalizedPhone,
          empresa_id: empresaIdFromUrl ? parseInt(empresaIdFromUrl) : scanResult?.empresa_id
        })
      });

      const data = await response.json();

      if (data.success) {
        setMotorista(data.data);
        await createChecklist(data.data);
      } else {
        setError(data.message || 'Motorista não encontrado');
        setStep('identify');
      }
    } catch (err) {
      console.error('Erro ao identificar motorista:', err);
      setError('Erro ao identificar motorista. Verifique o número.');
      setStep('identify');
    } finally {
      setIsLoading(false);
    }
  };

  const createChecklist = async (motoristaData: MotoristaInfo) => {
    if (!scanResult) return;

    try {
      setIsLoading(true);

      const response = await fetch(`${API_BASE_URL}/api/public/qr/checklist`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          motorista_telefone: motoristaData.celular,
          tipo_identificacao: scanResult.tipo,
          identificacao_id: scanResult.id
        })
      });

      const data = await response.json();

      if (data.success) {
        setStep('success');

        // Redirecionar para WhatsApp após 3 segundos
        setTimeout(() => {
          const whatsappUrl = `https://wa.me/55${motoristaData.celular.replace(/\D/g, '')}`;
          window.location.href = whatsappUrl;
        }, 3000);
      } else {
        setError(data.message || 'Erro ao iniciar checklist');
        setStep('error');
      }
    } catch (err) {
      console.error('Erro ao criar checklist:', err);
      setError('Erro ao iniciar checklist. Tente novamente.');
      setStep('error');
    } finally {
      setIsLoading(false);
    }
  };

  const handleManualSubmit = async () => {
    if (!manualTag.trim()) {
      setError('Digite a TAG do veículo ou equipamento');
      return;
    }

    // Enviar TAG para busca - backend irá identificar se é veículo ou equipamento
    const tagSearchData = JSON.stringify({
      tag: manualTag.trim().toUpperCase()
    });

    await processQRCode(tagSearchData);
  };

  const handlePhoneSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!telefone.trim()) {
      setError('Digite seu número de telefone');
      return;
    }
    await identifyMotorista(telefone);
  };

  const formatPhone = (value: string) => {
    const numbers = value.replace(/\D/g, '');
    if (numbers.length <= 2) return numbers;
    if (numbers.length <= 7) return `(${numbers.slice(0, 2)}) ${numbers.slice(2)}`;
    return `(${numbers.slice(0, 2)}) ${numbers.slice(2, 7)}-${numbers.slice(7, 11)}`;
  };

  return (
    <div style={{
      minHeight: '100vh',
      backgroundColor: '#f5f5f5',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      padding: '20px'
    }}>
      {/* Header */}
      <div style={{
        textAlign: 'center',
        marginBottom: '20px'
      }}>
        <h1 style={{ color: '#04C64F', margin: 0, fontSize: '24px' }}>ConfereAí</h1>
        <p style={{ color: '#666', margin: '5px 0 0' }}>Identificação de Veículo/Equipamento</p>
      </div>

      {/* Etapa: Scan QR Code */}
      {step === 'scan' && (
        <div style={{
          width: '100%',
          maxWidth: '400px',
          backgroundColor: '#fff',
          borderRadius: '10px',
          padding: '20px',
          boxShadow: '0 2px 10px rgba(0,0,0,0.1)'
        }}>
          {hasCamera && !showManualInput ? (
            <>
              <div style={{
                position: 'relative',
                width: '100%',
                aspectRatio: '1',
                backgroundColor: '#000',
                borderRadius: '10px',
                overflow: 'hidden',
                marginBottom: '20px'
              }}>
                <video
                  ref={videoRef}
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover'
                  }}
                  playsInline
                  muted
                />
                <canvas ref={canvasRef} style={{ display: 'none' }} />

                {/* Overlay de scan */}
                <div style={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                  width: '200px',
                  height: '200px',
                  border: '3px solid #04C64F',
                  borderRadius: '10px'
                }} />
              </div>

              {!isScanning ? (
                <button
                  onClick={startCamera}
                  style={{
                    width: '100%',
                    padding: '15px',
                    backgroundColor: '#04C64F',
                    color: '#fff',
                    border: 'none',
                    borderRadius: '8px',
                    fontSize: '16px',
                    cursor: 'pointer'
                  }}
                >
                  📷 Iniciar Câmera
                </button>
              ) : (
                <p style={{ textAlign: 'center', color: '#666' }}>
                  Aponte a câmera para o QR Code do veículo
                </p>
              )}

              <button
                onClick={() => setShowManualInput(true)}
                style={{
                  width: '100%',
                  padding: '10px',
                  marginTop: '10px',
                  backgroundColor: 'transparent',
                  color: '#666',
                  border: '1px solid #ddd',
                  borderRadius: '8px',
                  fontSize: '14px',
                  cursor: 'pointer'
                }}
              >
                Digitar TAG manualmente
              </button>
            </>
          ) : (
            <>
              <h3 style={{ textAlign: 'center', marginBottom: '20px' }}>
                Digite a TAG do Veículo/Equipamento
              </h3>

              <input
                type="text"
                value={manualTag}
                onChange={(e) => setManualTag(e.target.value.toUpperCase())}
                placeholder="Ex: ABC-1234 ou EQP-001"
                style={{
                  width: '100%',
                  padding: '15px',
                  fontSize: '18px',
                  textAlign: 'center',
                  border: '2px solid #ddd',
                  borderRadius: '8px',
                  marginBottom: '15px'
                }}
              />

              <button
                onClick={handleManualSubmit}
                disabled={isLoading}
                style={{
                  width: '100%',
                  padding: '15px',
                  backgroundColor: '#04C64F',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '16px',
                  cursor: 'pointer',
                  opacity: isLoading ? 0.7 : 1
                }}
              >
                {isLoading ? 'Buscando...' : 'Buscar'}
              </button>

              {hasCamera && (
                <button
                  onClick={() => setShowManualInput(false)}
                  style={{
                    width: '100%',
                    padding: '10px',
                    marginTop: '10px',
                    backgroundColor: 'transparent',
                    color: '#666',
                    border: '1px solid #ddd',
                    borderRadius: '8px',
                    fontSize: '14px',
                    cursor: 'pointer'
                  }}
                >
                  Usar câmera
                </button>
              )}
            </>
          )}

          {error && (
            <p style={{ color: '#e74c3c', textAlign: 'center', marginTop: '15px' }}>
              {error}
            </p>
          )}
        </div>
      )}

      {/* Etapa: Identificar Motorista */}
      {step === 'identify' && scanResult && (
        <div style={{
          width: '100%',
          maxWidth: '400px',
          backgroundColor: '#fff',
          borderRadius: '10px',
          padding: '20px',
          boxShadow: '0 2px 10px rgba(0,0,0,0.1)'
        }}>
          {/* Dados do veículo/equipamento */}
          <div style={{
            backgroundColor: '#f8f9fa',
            borderRadius: '8px',
            padding: '15px',
            marginBottom: '20px',
            textAlign: 'center'
          }}>
            <p style={{ color: '#04C64F', fontWeight: 'bold', marginBottom: '5px' }}>
              {scanResult.tipo === 'VEICULO' ? '🚛 Veículo Identificado' : '🔧 Equipamento Identificado'}
            </p>
            <h2 style={{ margin: '10px 0', fontSize: '24px' }}>
              {scanResult.tipo === 'VEICULO' ? scanResult.placa : scanResult.nome}
            </h2>
            <p style={{ color: '#666', margin: 0 }}>
              {scanResult.tipo === 'VEICULO'
                ? `${scanResult.marca} ${scanResult.modelo}`
                : scanResult.tipo_equipamento}
            </p>
          </div>

          {/* Formulário de identificação */}
          <form onSubmit={handlePhoneSubmit}>
            <label style={{ display: 'block', marginBottom: '10px', color: '#333' }}>
              Informe seu número de telefone:
            </label>
            <input
              type="tel"
              value={telefone}
              onChange={(e) => setTelefone(formatPhone(e.target.value))}
              placeholder="(85) 99999-9999"
              style={{
                width: '100%',
                padding: '15px',
                fontSize: '18px',
                textAlign: 'center',
                border: '2px solid #ddd',
                borderRadius: '8px',
                marginBottom: '15px'
              }}
            />

            <button
              type="submit"
              disabled={isLoading}
              style={{
                width: '100%',
                padding: '15px',
                backgroundColor: '#04C64F',
                color: '#fff',
                border: 'none',
                borderRadius: '8px',
                fontSize: '16px',
                cursor: 'pointer',
                opacity: isLoading ? 0.7 : 1
              }}
            >
              {isLoading ? 'Verificando...' : 'Continuar'}
            </button>
          </form>

          {error && (
            <p style={{ color: '#e74c3c', textAlign: 'center', marginTop: '15px' }}>
              {error}
            </p>
          )}
        </div>
      )}

      {/* Etapa: Sucesso */}
      {step === 'success' && motorista && scanResult && (
        <div style={{
          width: '100%',
          maxWidth: '400px',
          backgroundColor: '#fff',
          borderRadius: '10px',
          padding: '30px',
          boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
          textAlign: 'center'
        }}>
          <div style={{
            width: '80px',
            height: '80px',
            backgroundColor: '#04C64F',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 20px',
            fontSize: '40px'
          }}>
            ✓
          </div>

          <h2 style={{ color: '#04C64F', marginBottom: '10px' }}>
            Checklist Iniciado!
          </h2>

          <p style={{ color: '#666', marginBottom: '20px' }}>
            Olá, <strong>{motorista.nome}</strong>!
          </p>

          <div style={{
            backgroundColor: '#f8f9fa',
            borderRadius: '8px',
            padding: '15px',
            marginBottom: '20px'
          }}>
            <p style={{ margin: '5px 0', color: '#333' }}>
              <strong>{scanResult.tipo === 'VEICULO' ? 'Veículo' : 'Equipamento'}:</strong>{' '}
              {scanResult.tipo === 'VEICULO' ? scanResult.placa : scanResult.nome}
            </p>
          </div>

          <p style={{ color: '#666', fontSize: '14px' }}>
            Redirecionando para o WhatsApp...
          </p>

          <div style={{
            marginTop: '20px',
            padding: '15px',
            backgroundColor: '#25D366',
            borderRadius: '8px',
            color: '#fff'
          }}>
            📱 Continue o checklist pelo WhatsApp
          </div>
        </div>
      )}

      {/* Etapa: Erro */}
      {step === 'error' && (
        <div style={{
          width: '100%',
          maxWidth: '400px',
          backgroundColor: '#fff',
          borderRadius: '10px',
          padding: '30px',
          boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
          textAlign: 'center'
        }}>
          <div style={{
            width: '80px',
            height: '80px',
            backgroundColor: '#e74c3c',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 20px',
            fontSize: '40px',
            color: '#fff'
          }}>
            ✕
          </div>

          <h2 style={{ color: '#e74c3c', marginBottom: '10px' }}>
            Erro
          </h2>

          <p style={{ color: '#666', marginBottom: '20px' }}>
            {error || 'Ocorreu um erro ao processar sua solicitação.'}
          </p>

          <button
            onClick={() => {
              setStep('scan');
              setError(null);
              setScanResult(null);
              setMotorista(null);
            }}
            style={{
              padding: '15px 30px',
              backgroundColor: '#04C64F',
              color: '#fff',
              border: 'none',
              borderRadius: '8px',
              fontSize: '16px',
              cursor: 'pointer'
            }}
          >
            Tentar novamente
          </button>
        </div>
      )}

      {/* Footer */}
      <p style={{
        marginTop: '30px',
        color: '#999',
        fontSize: '12px',
        textAlign: 'center'
      }}>
        Powered by ConfereAí
      </p>
    </div>
  );
};

export default QRScanner;
